import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Alert,
  Skeleton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import { useUserRoles } from '../auth/ProtectedRoute';
import userApi, { User } from '../api/userApi';
import logger from '../utils/logger';

interface DashboardStats {
  total_users: number;
  active_sessions: number;
  pending_tasks: number;
  system_health: 'healthy' | 'degraded' | 'unhealthy';
}

interface RecentActivity {
  id: string;
  action: string;
  timestamp: string;
  details: string;
}

const Home: React.FC = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const userRoles = useUserRoles();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user profile
      const profile = await userApi.getCurrentUser();
      setUserProfile(profile);

      // Mock dashboard stats (replace with actual API calls)
      const mockStats: DashboardStats = {
        total_users: 1250,
        active_sessions: 89,
        pending_tasks: 15,
        system_health: 'healthy',
      };
      setStats(mockStats);

      // Mock recent activity (replace with actual API calls)
      const mockActivity: RecentActivity[] = [
        {
          id: '1',
          action: 'Profile Updated',
          timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          details: 'Updated contact information',
        },
        {
          id: '2',
          action: 'Login',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          details: 'Successful login from Chrome browser',
        },
        {
          id: '3',
          action: 'Password Changed',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          details: 'Password updated successfully',
        },
      ];
      setRecentActivity(mockActivity);

      logger.userAction('dashboard_loaded', {
        user_id: profile.id,
        roles: userRoles,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
      logger.error('Dashboard load failed', {
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {authUser?.profile?.name || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your account and the system.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button
            onClick={loadDashboardData}
            size="small"
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* User Profile Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  User Profile
                </Typography>
              </Box>
              
              {loading ? (
                <Box>
                  <Skeleton variant="circular" width={80} height={80} />
                  <Skeleton variant="text" sx={{ mt: 2 }} />
                  <Skeleton variant="text" />
                </Box>
              ) : userProfile ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{ width: 80, height: 80, mr: 2 }}
                      src={authUser?.profile?.picture}
                      alt={userProfile?.name || 'User'}
                    >
                      {(userProfile?.name || userProfile?.email || 'U')?.charAt?.(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{userProfile?.name || 'Unknown User'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userProfile.email}
                      </Typography>
                      {userProfile.department && (
                        <Chip
                          label={userProfile.department}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {userRoles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        variant="outlined"
                        size="small"
                        icon={<SecurityIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* System Stats */}
        {userRoles.includes('admin') || userRoles.includes('manager') ? (
          <Grid item xs={12} md={6} lg={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2">
                    System Overview
                  </Typography>
                </Box>
                
                {loading ? (
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                      <Grid item xs={6} md={3} key={i}>
                        <Skeleton variant="rectangular" height={80} />
                      </Grid>
                    ))}
                  </Grid>
                ) : stats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {stats.total_users}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Users
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {stats.active_sessions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Sessions
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                          {stats.pending_tasks}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pending Tasks
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Chip
                          label={stats.system_health.toUpperCase()}
                          color={getHealthStatusColor(stats.system_health) as any}
                          size="medium"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          System Health
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Recent Activity
                </Typography>
              </Box>
              
              {loading ? (
                <List>
                  {[1, 2, 3].map((i) => (
                    <ListItem key={i}>
                      <ListItemIcon>
                        <Skeleton variant="circular" width={24} height={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Skeleton variant="text" />}
                        secondary={<Skeleton variant="text" />}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <List>
                  {recentActivity.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem>
                        <ListItemIcon>
                          <AssignmentIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.action}
                          secondary={`${activity.details} • ${formatRelativeTime(activity.timestamp)}`}
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Quick Actions
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => logger.userAction('view_profile_clicked')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    Update Profile
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => logger.userAction('view_settings_clicked')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <SecurityIcon sx={{ mr: 1 }} />
                    Security Settings
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => logger.userAction('view_activity_clicked')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <ScheduleIcon sx={{ mr: 1 }} />
                    View Activity Log
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;